/**
 * @fileoverview Focus Header Component
 * 
 * Displays the header section of the focus view with student name and action controls.
 * 
 * @module vtc/components/Focus/FocusHeader
 */
import { useEffect, useState } from 'react';
import {
  Text,
  IconButton,
  IconExitFullScreenLine,
  IconSettingsLine,
  Flex,
  IconExternalLinkLine,
  IconNoLine,
  IconSyllabusLine,
  IconRefreshLine,
  IconAdminToolsLine,
  IconEducatorsLine,
  Icon,
  useIamValidation,
  Button
} from '@smarterservices/smarter-ui';
import { Menu } from '@instructure/ui';
import Card from '../Common/Card';
import { ButtonWithConfirmation } from '@ui/shared/components/Common/ButtonWithConfirmation';
import AssistCheckinReminder from '../Modal/AssistCheckinReminder';
import AssistCheckinDisconnectNotice from '../Modal/AssistCheckinDisconnectNotice';
import useAppStore from '../../stores/app';
import useSessionStore from '../../stores/session';
import SessionFocusIndicator from '../SessionCard/SessionFocusIndicator';
import TransferNote from '../Checkin/TransferNote';
import SessionConnectionIndicator from '../SessionCard/SessionConnectionIndicator';

/**
 * FocusHeader Component
 * 
 * Renders the header for the focus view with student information and action buttons.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.focusedSession - Currently focused session data
 * @param {string} props.focusedSession.firstName - Student's first name
 * @param {string} props.focusedSession.lastName - Student's last name
 * @param {Function} props.onTransfer - Handler for transfer action
 * @param {Function} props.onEndSession - Handler for end session action
 * @param {Function} props.onClose - Handler for closing focus view
 * @returns {JSX.Element}
 */
function FocusHeader({
  focusedSession,
  onTransfer,
  onEndSession,
  onIncidentReport,
  onClose,
}) {
  // Note: We don't need to subscribe to stores here since we only call actions
  // Actions can be accessed via getState() when needed to avoid re-renders

  const { checkPermission } = useIamValidation();

  // Check if current user is the reviewer by checking session.sessionData.reviewBy
  const currentUserSid = useAppStore.getState()?.user?.sid;
  const focusSessionSid = useSessionStore.getState()?.focusedSession?.sessionSid;
  const isReviewing = useSessionStore.getState()?.sessions[focusSessionSid]?.sessionData?.reviewBy === currentUserSid;

  /**
   * State for controlling the assist check-in reminder modal visibility.
   * Shows when a proctor joins a session that is already being checked in by another proctor.
   */
  const [showAssistCheckinReminder, setShowAssistCheckinReminder] = useState(false);

  /**
   * State for controlling the proctor disconnected notice modal visibility.
   * Shows when the primary proctor disconnects and the assisting proctor needs to be informed.
   */
  const [showProctorDisconnectedNotice, setShowProctorDisconnectedNotice] = useState(false);

  /**
   * Effect: Show the assist check-in reminder modal when entering a session in review mode
   * but the current user is not the primary reviewer. This informs the assisting proctor
   * about the limitations and behavior of assisting in a check-in.
   */
  useEffect(() => {
    if (focusedSession?.location === 'review' && !isReviewing) {
      setShowAssistCheckinReminder(true);
    }
  }, [focusedSession?.location, isReviewing]);

  /**
   * Handler to dismiss the assist check-in reminder modal when user clicks cancel.
   */
  const handleCancelAssistCheckinReminder = () => {
    setShowAssistCheckinReminder(false);
  };

  /**
   * Handler to dismiss the assist check-in reminder modal when user confirms.
   * Same behavior as cancel - just dismisses the modal.
   */
  const handleConfirmedAssistCheckinReminder = () => {
    setShowAssistCheckinReminder(false);
  };

  /**
   * Effect: Monitor for session transitions that require notifying the assisting proctor.
   * When a session is put back into 'waiting' or 'sentback' state and the current user
   * is not the primary reviewer, this indicates the primary proctor has disconnected or
   * released the session. Shows a disconnect notice to inform the assisting proctor that
   * the session is no longer being actively reviewed.
   */
  useEffect(() => {
    if (['waiting', 'sentback'].includes(focusedSession?.location) && !isReviewing) {
      setShowProctorDisconnectedNotice(true);
    }
  }, [focusedSession?.location, isReviewing]);

  /**
   * Handler for when the proctor disconnected notice is closed.
   * Dismisses the modal and then closes the focus view.
   */
  const handleProctorDisconnectedClose = () => {
    setShowProctorDisconnectedNotice(false);
    onClose();
  };

  return (
    <Card.Header>
      <Card.Title>
        <Text weight="bold">
          {focusedSession?.firstName} {focusedSession?.lastName}
        </Text>
      </Card.Title>
      {(focusedSession?.transfer?.note && focusedSession?.location === 'review') && (
        <Flex.Item>
          <TransferNote note={focusedSession.transfer?.note} />
        </Flex.Item>
      )}

      <Flex.Item as="div" shouldGrow margin='none small none none' textAlign='end'>
        {/* Focus indicator - shows who is focused on this session */}

        <SessionFocusIndicator
          sessionSid={focusedSession?.sessionSid}
        />

        <Flex.Item margin='none none none small'>
          <SessionConnectionIndicator sessionSid={focusedSession?.sessionSid} />
        </Flex.Item>


      </Flex.Item>
      <Card.Actions>

        {(focusedSession?.location === 'waiting' && !isReviewing) &&
          <Button
            withBackground={true}
            withBorder={true}
            color="success"
            size="small"
            screenReaderLabel="Start Check-in"
            margin="none x-small none none"
            padding="xx-small"
            onClick={() => useSessionStore.getState().setFocusedSession(focusedSession?.sessionSid)}
          >
            Start Check-in
          </Button>
        }



        {/* Settings menu */}
        <Menu
          placement="bottom end"
          shouldContainFocus
          shouldReturnFocus
          mountNode={() => document.body}
          trigger={
            <IconButton
              withBackground={false}
              withBorder={false}
              color="secondary"
              size="small"
              screenReaderLabel="Session Options"
              margin="none xx-small none none"
              padding="none"
            >
              <IconSettingsLine />
            </IconButton>
          }
        >

          <Menu.Item onSelect={() => useSessionStore.getState().openSessionLog(focusedSession?.sessionSid)}>
            <Flex alignItems="center">
              <Flex.Item margin="0 xx-small 0 0">
                <IconSyllabusLine size="x-small" />
              </Flex.Item>
              <Flex.Item>
                <Text>View Session Log</Text>
              </Flex.Item>
            </Flex>
          </Menu.Item>


          <Menu.Item onSelect={() => useSessionStore.getState().openDiagnostics(focusedSession?.sessionSid)}>
            <Flex alignItems="center">
              <Flex.Item margin="0 xx-small 0 0">
                <IconAdminToolsLine size="x-small" />
              </Flex.Item>
              <Flex.Item>
                <Text>Session Diagnostics</Text>
              </Flex.Item>
            </Flex>
          </Menu.Item>

          <Menu.Item onSelect={() => useSessionStore.getState().openRemoteControl(focusedSession?.sessionSid)}>
            <Flex alignItems="center">
              <Flex.Item margin="0 xx-small 0 0">
                <IconEducatorsLine size="x-small" />
              </Flex.Item>
              <Flex.Item>
                <Text>Remote Control</Text>
              </Flex.Item>
            </Flex>
          </Menu.Item>

          {/* Transfer session */}
          {['testing'].includes(focusedSession?.location) &&
            <Menu.Item onSelect={onTransfer}>
              <Flex alignItems="center">
                <Flex.Item margin="0 xx-small 0 0">
                  <IconExternalLinkLine size="x-small" />
                </Flex.Item>
                <Flex.Item>
                  <Text>Transfer</Text>
                </Flex.Item>
              </Flex>
            </Menu.Item>
          }
          {/* Incident Report - only show if in the testing location */}
          {['checkout', 'testing'].includes(focusedSession?.location) &&
            <Menu.Item onSelect={onIncidentReport}>
              <Flex alignItems="center">
                <Flex.Item margin="0 xx-small 0 0">
                  <Icon name="IconFileTextShield" size="x-small" />
                </Flex.Item>
                <Flex.Item>
                  <Text>Incident Report</Text>
                </Flex.Item>
              </Flex>
            </Menu.Item>
          }
          {/* End session */}
          {['testing'].includes(focusedSession?.location) &&
            <Menu.Item onSelect={onEndSession}>
              <Flex alignItems="center">
                <Flex.Item margin="0 xx-small 0 0">
                  <IconNoLine size="x-small" />
                </Flex.Item>
                <Text>End Session</Text>
              </Flex>
            </Menu.Item>
          }
          <Menu.Separator />
          <Menu.Item onSelect={() => useSessionStore.getState().reinitializeSession(focusedSession?.sessionSid)}>
            <Flex alignItems="center">
              <Flex.Item margin="0 xx-small 0 0">
                <IconRefreshLine size="x-small" />
              </Flex.Item>
              <Text>Reload Session</Text>
            </Flex>
          </Menu.Item>
        </Menu>

        {/* Exit focus mode button */}
        {(focusedSession?.location === 'review' && isReviewing) ?
          <ButtonWithConfirmation
            variant="button"
            onConfirm={() => {
              // Clear the confirmation b/c the confirmation button handles it...
              useAppStore.getState().setDrawerCloseConfirmation(null);
              // now trigger the on close function...
              onClose();
            }}
            confirmationText="Are you sure you want put this session back in checkin?"
            confirmButtonText="Yes"
            cancelButtonText="No"
            confirmButtonColor="danger"
            color="secondary"
            screenReaderLabel="Exit Focus Mode"
            tooltipProps={{
              placement: 'top',
              color: 'secondary',
            }}
          >
            Return To Waiting
          </ButtonWithConfirmation>
          :
          <IconButton
            withBackground={true}
            withBorder={true}
            color="secondary"
            size="small"
            screenReaderLabel="Exit Focus Mode"
            margin="none x-small none none"
            padding="xx-small"
            onClick={onClose}
          >
            <IconExitFullScreenLine />
          </IconButton>
        }
      </Card.Actions>

      <AssistCheckinReminder
        show={showAssistCheckinReminder}
        sessionSid={focusedSession?.sessionSid}
        onCancel={handleCancelAssistCheckinReminder}
        onConfirmation={handleConfirmedAssistCheckinReminder}
      />

      <AssistCheckinDisconnectNotice
        show={showProctorDisconnectedNotice}
        onClose={handleProctorDisconnectedClose}
      />
    </Card.Header>
  );
}

export default FocusHeader;
